import React, { useCallback } from 'react';
import { Spin, Input, Form, Tooltip, message, Image, Modal } from 'antd';
import {
    PlusOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    BugOutlined,
    AimOutlined,
    EditOutlined,
    RocketOutlined,
    CrownOutlined,
    ThunderboltOutlined,
    CheckCircleFilled,
    CloseOutlined
} from '@ant-design/icons';
import { Button } from '@/components/ui/Button';
import { MultipleButton, SegmentedButton } from '@/components/ui/MultipleButton';
import { colors } from '@/utils/colorSystem';
import { Container } from '@/components/ui/Container';
import StatusBadge from '@/components/ui/StatusBadge';
import ProjectMembersModal from '@components/module/projects/detail_tab/ProjectMembersModal';
import AddUrlModal from '@components/module/projects/detail_tab/AddUrlModal';
import ProjectDescriptionContent from '@components/module/projects/detail_tab/ProjectDescriptionContent';
import ProjectAttachmentsPanel from '@components/module/projects/detail_tab/ProjectAttachmentsPanel';
import { useProjectDetailsTabLogic } from '@hooks/projects/detail/useProjectDetailsTab';
import {
    resolveProfileImageUrl,
    getMemberRoleLabel,
    handleAttachmentItemClick,
    handleMarkdownLinkClick
} from '@hooks/projects/detail/useProjectDetail';
import {
    getInitials,
    getAvatarColor,
    resolveAttachmentUrl
} from '@hooks/projects/projectHelpers';
import { ENV } from '@configs/env';
import InviteMemberModal from '@components/module/projects/contributors_tab/InviteMemberModal';
import CreateUserFormModal from '@components/module/projects/contributors_tab/CreateUserFormModal';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { SliderTab } from '@/components/ui/SliderTab';
import SidePanel from '@/components/ui/SidePanel/SidePanel';
import {
    ContentCard,
    KPICardsGrid,
    DetailsTabLayout,
    StickySubTabsUnderlineRow,
    SubTabsUnderlineTabsGroup,
    SubTabsUnderlineButton,
    TwoColumnGrid,
    AvatarStackContainer,
    RolesSliderTabWrap,
} from '../style';
import KpiCard from '@components/ui/analytics/KpiCard';
import SprintProgressCard from '@components/module/projects/detail_tab/SprintProgressCard';
import TeamActivityCard from '@components/module/projects/detail_tab/TeamActivityCard';
import TeamWorkloadCard from '@components/module/projects/detail_tab/TeamWorkloadCard';
import TeamActivityPanel from '@components/module/projects/detail_tab/TeamActivityPanel';
import UserAvatar from '@/components/ui/UserAvatar';
import { useUserDetail } from '@hooks/users/useUserDetail';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_TEAM_ACTIVITY_VIEW, PROJECT_TEAM_WORKLOAD_VIEW } from '@/ability';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return dateStr;
    }
};

export default function ProjectDetailsTab({ projectId }) {
    const can = useCanCheck();
    const canViewAllActivity = can(PROJECT_TEAM_ACTIVITY_VIEW);
    const canViewAllWorkload = can(PROJECT_TEAM_WORKLOAD_VIEW);

    const [selectedUserId, setSelectedUserId] = React.useState(null);
    const [selectedUserFallback, setSelectedUserFallback] = React.useState(null);
    const [profilePanelOpen, setProfilePanelOpen] = React.useState(false);
    const [profileActiveTab, setProfileActiveTab] = React.useState('info');
    const [activityPanelOpen, setActivityPanelOpen] = React.useState(false);
    const [summaryView, setSummaryView] = React.useState('mine');
    
    const [isLargeScreen, setIsLargeScreen] = React.useState(true);
    React.useEffect(() => {
        const checkScreen = () => {
            setIsLargeScreen(window.innerWidth >= 1024);
        };
        checkScreen();
        window.addEventListener('resize', checkScreen);
        return () => window.removeEventListener('resize', checkScreen);
    }, []);

    const maxVisibleMembers = isLargeScreen ? 5 : 3;
    
    const { data: detailedUser, isLoading: loadingUser } = useUserDetail(selectedUserId);
    const {
        navigate,
        projectLoading,
        projectError,
        project,
        isClientRole,
        canViewClientLine,
        isInvitedCollaborator,
        showDetailAndAttachmentActions,
        savePending,
        uploadAttachmentsPending,
        activeTab,
        setActiveTab,
        previewImageUrl,
        setPreviewImageUrl,
        attachmentInputRef,
        isEditingDetail,
        setIsEditingDetail,
        editContent,
        setEditContent,
        pendingPostAttachments,
        pendingLinkAttachments,
        removePendingLinkAttachment,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        attachUrlsExpanded,
        setAttachUrlsExpanded,
        attachUrlModalOpen,
        setAttachUrlModalOpen,
        attachUrlForm,
        membersModalOpen,
        setMembersModalOpen,
        membersSearch,
        setMembersSearch,
        isClientChipExpanded,
        setIsClientChipExpanded,
        hasContent,
        projectDetailsContentLinked,
        showProjectDetailsAsMarkdown,
        detailsMarkdownAttachmentItems,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        assignedUsers,
        visibleAssignedUsers,
        linkAttachments,
        fileAttachments,
        handleAttachmentAddClick,
        handleAttachmentFileChange,
        handlePostFilesSelected,
        removePendingPostAttachment,
        handleLinkRequest,
        handleAddAttachUrl,
        handleAttachUrlModalSubmit,
        handleLinkModalSubmit,
        handleSaveDetail,
        handleCancelEdit,
        progressPercent,
        datesDisplay,
        clientUser,
        showStatusClock,
        projectDetailsContent,
        contributors,
        descriptionDraft,
        handleResetDraft,
        hasServerContent,
        isEditing,
        deletedSavedUrls,
        handleDeleteSavedAttachment,
        deleteAttachmentMutation,
        formattedProjectType,
        analytics,
        analyticsLoading,
        teamActivityData,
    } = useProjectDetailsTabLogic(projectId);

    const {
        inviteModalOpen,
        openInviteModal,
        closeInviteModal,
        inviteForm,
        inviteSelectOptions,
        filterSelectOption,
        addContributorsPending,
        handleInviteSubmit,
        canCreateUser,
        createUserModalOpen,
        openCreateUserModal,
        closeCreateUserModal,
        createForm,
        roleSelectOptions,
        createUserPending,
        handleCreateUserSubmit,
        removeContributor,
        removeContributorPending,
    } = contributors;

    const handleTabChange = useCallback((key) => {
        setActiveTab(key);
    }, [setActiveTab]);

    const handleAttachmentClick = useCallback((att) => {
        handleAttachmentItemClick(att, setPreviewImageUrl, resolveAttachmentUrl);
    }, [setPreviewImageUrl]);

    const handleMarkdownDescriptionLinkClick = useCallback(
        (args) => {
            handleMarkdownLinkClick(args, project?.projectDetails?.attachments ?? [], handleAttachmentClick);
        },
        [project?.projectDetails?.attachments, handleAttachmentClick],
    );

    const handleDeleteAttachmentRedirect = useCallback((att) => {
        if (!att) return;
        const label = att.name || att.title || 'Attachment';
        const hide = message.loading(`Deleting "${label}"…`, 0);
        const params = att._fromProject ? { id: att.id } : { url: att.url };
        deleteAttachmentMutation.mutate(params, {
            onSuccess: () => {
                hide();
                message.success(`"${label}" deleted successfully.`);
            },
            onError: (err) => {
                hide();
                message.error(err?.response?.data?.message || err?.message || `Failed to delete "${label}".`);
            },
        });
    }, [deleteAttachmentMutation]);

    const handleDeleteLinkRedirect = useCallback((a) => {
        if (!a) return;
        const label = a.title || a.name || 'Link';
        const hide = message.loading(`Deleting "${label}"…`, 0);
        const params = a._fromProject ? { id: a.id } : { url: a.url };
        deleteAttachmentMutation.mutate(params, {
            onSuccess: () => {
                hide();
                message.success(`"${label}" deleted successfully.`);
            },
            onError: (err) => {
                hide();
                message.error(err?.response?.data?.message || err?.message || `Failed to delete "${label}".`);
            },
        });
    }, [deleteAttachmentMutation]);


    if (projectLoading) {
        return (
            <Container
                $display="flex"
                $justifyContent="center"
                $alignItems="center"
                $flex="1"
                $width="100%"
                style={{ padding: '80px 0', background: 'transparent' }}
            >
                <Spin size="large" />
            </Container>
        );
    }

    if (projectError || !project) {
        return (
            <ContentCard $padding="24px">
                <span style={{ opacity: 0.65 }}>Failed to load project details.</span>
            </ContentCard>
        );
    }

    // Determine the progress analytics based on project capabilities
    const capabilities = project?.capabilities ?? {};
    let progressTitle = '';
    let progressAnalytics = null;

    if (capabilities.useSprints) {
        progressTitle = `Current Sprint: ${analytics?.currentSprintAnalytics?.name || 'N/A'}`;
        progressAnalytics = analytics?.currentSprintAnalytics;
    } else if (capabilities.useMilestones) {
        progressTitle = `Current Milestone: ${analytics?.currentMilestoneAnalytics?.name || 'N/A'}`;
        progressAnalytics = analytics?.currentMilestoneAnalytics;
    } else if (capabilities.usePhases) {
        progressTitle = `Current Phase: ${analytics?.currentPhaseAnalytics?.name || 'N/A'}`;
        progressAnalytics = analytics?.currentPhaseAnalytics;
    } else {
        progressTitle = 'All Project Tasks';
        progressAnalytics = analytics?.overallTaskAnalytics;
    }

    const completedTasks = progressAnalytics?.taskCompleted !== undefined && progressAnalytics?.taskCompleted !== null ? progressAnalytics.taskCompleted : 'N/A';
    const inProgressTasks = progressAnalytics?.taskInProgress !== undefined && progressAnalytics?.taskInProgress !== null ? progressAnalytics.taskInProgress : 'N/A';
    const pendingTasks = progressAnalytics?.taskPending !== undefined && progressAnalytics?.taskPending !== null ? progressAnalytics.taskPending : 'N/A';
    const totalProgressTasks = (typeof completedTasks === 'number' && typeof inProgressTasks === 'number' && typeof pendingTasks === 'number')
        ? completedTasks + inProgressTasks + pendingTasks
        : 'N/A';

    // Resolve Manager and PMO roles for the Project Manager section
    const isManagerOrPmo = (u) => {
        const roleLabel = getMemberRoleLabel(u).toLowerCase();
        if (roleLabel.includes('hr')) {
            return false;
        }
        return (
            roleLabel.includes('manager') ||
            roleLabel.includes('manage') ||
            roleLabel.includes('pmo') ||
            roleLabel.includes('pm')
        );
    };

    let managers = assignedUsers.filter(isManagerOrPmo);
    let teamMembersExcludingManager = assignedUsers.filter(u => !isManagerOrPmo(u));

    // Fallback if no manager is found
    if (managers.length === 0 && assignedUsers.length > 0) {
        managers = [assignedUsers[0]];
        teamMembersExcludingManager = assignedUsers.slice(1);
    }

    // Map team activity and workloads to the format expected by the cards
    const mappedActivities = React.useMemo(() => {
        const activitiesList = teamActivityData?.items || [];
        return activitiesList.map((item, idx) => {
            const userName = item.user?.name || 'Unknown User';
            const projectUser = assignedUsers.find(u => String(u.id || u.userId || u._id) === String(item.user?.id));
            const role = projectUser ? getMemberRoleLabel(projectUser) : 'Contributor';
            
            // Format time difference cleanly (e.g. "5m ago", "2h ago", "1d ago")
            let relativeTime = 'Just now';
            if (item.createdAt) {
                try {
                    const diffMs = Date.now() - new Date(item.createdAt).getTime();
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMins / 60);
                    const diffDays = Math.floor(diffHours / 24);
                    
                    if (diffDays > 0) relativeTime = `${diffDays}d ago`;
                    else if (diffHours > 0) relativeTime = `${diffHours}h ago`;
                    else if (diffMins > 0) relativeTime = `${diffMins}m ago`;
                } catch (e) {
                    relativeTime = 'Recent';
                }
            }

            let actionText = item.message || 'performed an action';
            if (item.message && userName && item.message.toLowerCase().startsWith(userName.toLowerCase())) {
                actionText = item.message.substring(userName.length).trim();
            }

            return {
                id: item.id || idx,
                user: userName,
                action: actionText,
                time: relativeTime,
                avatarBg: getAvatarColor(item.user?.id || idx),
                role,
                imageUrl: item.user?.avatarUrl ? resolveProfileImageUrl(item.user.avatarUrl, ENV.API_URL) : null,
                initials: getInitials(userName)
            };
        });
    }, [teamActivityData?.items, assignedUsers]);

    const mappedWorkloads = React.useMemo(() => {
        if (!analytics?.teamWorkload) return [];
        return analytics.teamWorkload.map((item, idx) => {
            const userName = item.user?.name || item.user?.email || 'Unknown User';
            const projectUser = assignedUsers.find(u => String(u.id || u.userId || u._id) === String(item.user?.id));
            const role = projectUser ? getMemberRoleLabel(projectUser) : 'Contributor';
            return {
                id: item.user?.id || idx,
                name: userName,
                completed: item.submittedCount,
                total: item.totalCount,
                avatarBg: getAvatarColor(item.user?.id || idx),
                role,
                imageUrl: item.user?.avatarUrl ? resolveProfileImageUrl(item.user.avatarUrl, ENV.API_URL) : null,
                initials: getInitials(userName),
                barBg: '#7543DC'
            };
        });
    }, [analytics?.teamWorkload, assignedUsers]);

    return (
        <DetailsTabLayout
            style={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                alignSelf: 'stretch',
            }}
        >
            <GlobalImagePreviewStyles />
            <Container
                $flex="1"
                $minHeight="0"
                $width="100%"
                $maxWidth="100%"
                $overflow="auto hidden"
                $display="flex"
                $flexDirection="column"
                $padding="0 0 24px"
            >
                <Container $width="100%" $paddingRight="24px">

                    {/* Title */}
                    <Container $marginBottom="20px">
                        <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--ant-color-text, #111827)', margin: '0 0 8px 0', fontFamily: 'var(--font-family-geist)' }}>
                            {project?.name || 'Project Name'}
                        </h2>
                    </Container>

                    {/* Slider Tabs */}
                    <RolesSliderTabWrap style={{ marginBottom: 24 }}>
                        <SliderTab
                            items={[
                                { key: 'info', label: 'Info' },
                                { key: 'description', label: 'Description' },
                                { key: 'attachments', label: 'Attachments' }
                            ]}
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            fitContent
                        />
                    </RolesSliderTabWrap>

                    {/* Info Tab Content */}
                    {activeTab === 'info' && (
                        <>
                            {/* KPI Toggle Row */}
                            <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginBottom="16px" $paddingRight="4px" $flexWrap="wrap" $gap="12px">
                                <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--ant-color-text, #111827)', fontFamily: 'var(--font-family-geist)' }}>
                                    {summaryView === 'mine' ? 'My Task Summary' : 'Total Project Summary'}
                                </span>
                                <RolesSliderTabWrap style={{ marginBottom: 0 }}>
                                    <SliderTab
                                        items={[
                                            { key: 'mine', label: 'My Summary' },
                                            { key: 'project', label: 'Project Summary' }
                                        ]}
                                        activeKey={summaryView}
                                        onChange={(key) => setSummaryView(key)}
                                        fitContent
                                    />
                                </RolesSliderTabWrap>
                            </Container>

                            {/* KPI Cards */}
                            <KPICardsGrid $columns={5} $height="auto" style={{ marginBottom: 20 }}>
                                <KpiCard
                                    icon={<CheckCircleOutlined style={{ color: '#10b981' }} />}
                                    value={
                                        summaryView === 'mine'
                                            ? (analytics?.myAnalytics?.taskCompleted !== undefined && analytics?.myAnalytics?.taskCompleted !== null ? analytics.myAnalytics.taskCompleted : '0')
                                            : (analytics?.taskCompleted !== undefined && analytics?.taskCompleted !== null ? analytics.taskCompleted : 'N/A')
                                    }
                                    subValue={
                                        summaryView === 'mine'
                                            ? `Total Project Completed: ${analytics?.taskCompleted !== undefined && analytics?.taskCompleted !== null ? analytics.taskCompleted : 'N/A'}`
                                            : `My Tasks Completed: ${analytics?.myAnalytics?.taskCompleted !== undefined && analytics?.myAnalytics?.taskCompleted !== null ? analytics.myAnalytics.taskCompleted : '0'}`
                                    }
                                    label={summaryView === 'mine' ? 'My Tasks Completed' : 'Tasks Completed'}
                                />
                                <KpiCard
                                    icon={<ClockCircleOutlined style={{ color: '#f59e0b' }} />}
                                    value={
                                        summaryView === 'mine'
                                            ? (analytics?.myAnalytics?.taskPending !== undefined && analytics?.myAnalytics?.taskPending !== null ? analytics.myAnalytics.taskPending : '0')
                                            : (analytics?.taskPending !== undefined && analytics?.taskPending !== null ? analytics.taskPending : 'N/A')
                                    }
                                    subValue={
                                        summaryView === 'mine'
                                            ? `Total Project Pending: ${analytics?.taskPending !== undefined && analytics?.taskPending !== null ? analytics.taskPending : 'N/A'}`
                                            : `My Tasks Pending: ${analytics?.myAnalytics?.taskPending !== undefined && analytics?.myAnalytics?.taskPending !== null ? analytics.myAnalytics.taskPending : '0'}`
                                    }
                                    label={summaryView === 'mine' ? 'My Tasks Pending' : 'Tasks Pending'}
                                />
                                <KpiCard
                                    icon={<WarningOutlined style={{ color: '#ef4444' }} />}
                                    value={
                                        summaryView === 'mine'
                                            ? (analytics?.myAnalytics?.overdueTasks !== undefined && analytics?.myAnalytics?.overdueTasks !== null ? analytics.myAnalytics.overdueTasks : '0')
                                            : (analytics?.overdueTasks !== undefined && analytics?.overdueTasks !== null ? analytics.overdueTasks : 'N/A')
                                    }
                                    subValue={
                                        summaryView === 'mine'
                                            ? `Total Project Overdue: ${analytics?.overdueTasks !== undefined && analytics?.overdueTasks !== null ? analytics.overdueTasks : 'N/A'}`
                                            : `My Tasks Overdue: ${analytics?.myAnalytics?.overdueTasks !== undefined && analytics?.myAnalytics?.overdueTasks !== null ? analytics.myAnalytics.overdueTasks : '0'}`
                                    }
                                    label={summaryView === 'mine' ? 'My Overdue Tasks' : 'Overdue Tasks'}
                                />
                                <KpiCard
                                    icon={<BugOutlined style={{ color: '#8b5cf6' }} />}
                                    value={
                                        summaryView === 'mine'
                                            ? (analytics?.myAnalytics?.totalBugs !== undefined && analytics?.myAnalytics?.totalBugs !== null ? analytics.myAnalytics.totalBugs : '0')
                                            : (analytics?.totalBugs !== undefined && analytics?.totalBugs !== null ? analytics.totalBugs : 'N/A')
                                    }
                                    subValue={
                                        summaryView === 'mine'
                                            ? `Total Project Bugs: ${analytics?.totalBugs !== undefined && analytics?.totalBugs !== null ? analytics.totalBugs : 'N/A'}`
                                            : `My Tasks Bugs: ${analytics?.myAnalytics?.totalBugs !== undefined && analytics?.myAnalytics?.totalBugs !== null ? analytics.myAnalytics.totalBugs : '0'}`
                                    }
                                    label={summaryView === 'mine' ? 'My Open Bugs' : 'Open Bugs'}
                                />
                                <KpiCard
                                    icon={<AimOutlined style={{ color: '#6366f1' }} />}
                                    value={analytics?.milestones?.formatted !== undefined && analytics?.milestones?.formatted !== null ? analytics.milestones.formatted : 'N/A'}
                                    label="Project Milestones"
                                />
                            </KPICardsGrid>

                            {/* Current Sprint Progress */}
                            <SprintProgressCard
                                title={progressTitle}
                                totalTasks={totalProgressTasks}
                                completedTasks={completedTasks}
                                inProgressTasks={inProgressTasks}
                                pendingTasks={pendingTasks}
                            />

                            {/* Project Details */}
                            <ContentCard $padding="24px" $marginBottom="20px">
                                {/* Dates Row */}
                                <Container $display="flex" $alignItems="center" $marginBottom="20px">
                                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)', width: 120 }}>Dates :</span>
                                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ant-color-text, #111827)' }}>{datesDisplay}</span>
                                </Container>

                                {/* Status Row */}
                                <Container $display="flex" $alignItems="center" $marginBottom="20px">
                                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)', width: 120 }}>Project Status :</span>
                                    <StatusBadge status={project.status} />
                                </Container>

                                {/* Project Type Row */}
                                <Container $display="flex" $alignItems="center" $marginBottom="20px">
                                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)', width: 120 }}>Project Type :</span>
                                    <span style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        padding: '4px 10px',
                                        borderRadius: 8,
                                        background: '#f3e8ff',
                                        color: '#6b21a8',
                                        border: '1px solid #e9d5ff'
                                    }}>
                                        {formattedProjectType || 'Standard SDLC'}
                                    </span>
                                </Container>

                                {/* Capabilities Row */}
                                <Container $display="flex" $alignItems="flex-start" $marginBottom="20px" $flexDirection="column" $gap="8px">
                                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Active Capabilities :</span>
                                    <Container $display="flex" $flexWrap="wrap" $gap="8px">
                                        {[
                                            { key: 'usePhases', label: 'Phases', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
                                            { key: 'useMilestones', label: 'Milestones', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
                                            { key: 'useSprints', label: 'Sprints', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
                                            { key: 'useTasks', label: 'Tasks', color: '#f59e0b', bg: '#fef3c7', border: '#fde68a' },
                                            { key: 'useSubmissionMemo', label: 'Submission Memo', color: '#06b6d4', bg: '#ecfeff', border: '#cffafe' },
                                            { key: 'clientSubmissionForApproval', label: 'Client Approval', color: '#ec4899', bg: '#fdf2f8', border: '#fbcfe8' },
                                            { key: 'useTimeLogs', label: 'Time Logs', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
                                        ].map(cap => {
                                            const active = project?.capabilities?.[cap.key] ?? true;
                                            return (
                                                <span key={cap.key} style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    padding: '2px 8px',
                                                    borderRadius: 6,
                                                    background: active ? cap.bg : '#f3f4f6',
                                                    color: active ? cap.color : '#9ca3af',
                                                    border: active ? `1px solid ${cap.border}` : '1px dashed #d1d5db',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                }}>
                                                    {cap.label} {active ? '✓' : '✗'}
                                                </span>
                                            );
                                        })}
                                    </Container>
                                </Container>

                                {/* Progress Row */}
                                <Container $display="flex" $flexDirection="column" $marginBottom="24px">
                                    <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginBottom="8px">
                                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Project Progress :</span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#8b5cf6' }}>{progressPercent}%</span>
                                    </Container>
                                    <Container $width="100%" $height="8px" $backgroundColor="#f3f4f6" $borderRadius="4px" $overflow="hidden">
                                        <Container $height="100%" $backgroundColor="#8b5cf6" $width={`${progressPercent}%`} $borderRadius="4px" />
                                    </Container>
                                </Container>

                                {/* Team and Manager Row */}
                                <Container $borderTop="1px solid var(--ant-color-border-secondary, #f0f0f0)" $paddingTop="20px" $display="flex" $alignItems="center" $gap="24px" $flexWrap="wrap">
                                    {/* Project Manager */}
                                    <Container $display="flex" $alignItems="center" $gap="12px">
                                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Project Manager :</span>
                                        <AvatarStackContainer>
                                            {managers.length > 0 ? (
                                                managers.map(pm => (
                                                    <span key={pm.id || pm.email} className="avatar-member">
                                                        <UserAvatar
                                                            name={pm.name || pm.email}
                                                            role={getMemberRoleLabel(pm)}
                                                            imageUrl={pm.profileImage ? resolveProfileImageUrl(pm.profileImage, ENV.API_URL) : null}
                                                            initials={getInitials(pm.name || pm.email)}
                                                            avatarBg={getAvatarColor(pm.id)}
                                                            size={50}
                                                            stacked={true}
                                                            onClick={() => {
                                                                setSelectedUserId(pm.id || pm._id || pm.userId);
                                                                setSelectedUserFallback(pm);
                                                                setProfilePanelOpen(true);
                                                                setProfileActiveTab('info');
                                                            }}
                                                        />
                                                    </span>
                                                ))
                                            ) : (
                                                <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>N/A</span>
                                            )}
                                        </AvatarStackContainer>
                                    </Container>

                                    {/* Separator */}
                                    <Container $width="1px" $height="36px" $backgroundColor="#e5e7eb" />

                                    {/* Team */}
                                    {!isClientRole && (
                                        <Container $display="flex" $alignItems="center" $gap="12px">
                                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Team :</span>
                                            <AvatarStackContainer>
                                                {teamMembersExcludingManager.slice(0, maxVisibleMembers).map(u => (
                                                    <span key={u.id || u.email} className="avatar-member">
                                                        <UserAvatar
                                                            name={u.name || u.email}
                                                            role={getMemberRoleLabel(u)}
                                                            imageUrl={u.profileImage ? resolveProfileImageUrl(u.profileImage, ENV.API_URL) : null}
                                                            initials={getInitials(u.name || u.email)}
                                                            avatarBg={getAvatarColor(u.id)}
                                                            size={50}
                                                            stacked={true}
                                                            onClick={() => {
                                                                setSelectedUserId(u.id || u._id || u.userId);
                                                                setSelectedUserFallback(u);
                                                                setProfilePanelOpen(true);
                                                                setProfileActiveTab('info');
                                                            }}
                                                            onRemove={
                                                                can('projects.members.manage') && String(u.id) !== String(project?.createdBy)
                                                                    ? () => Modal.confirm({
                                                                        title: 'Remove Member',
                                                                        content: `Are you sure you want to remove ${u.name || u.email} from this project?`,
                                                                        okText: 'Yes, Remove',
                                                                        cancelText: 'Cancel',
                                                                        okButtonProps: { danger: true },
                                                                        centered: true,
                                                                        onOk: () => removeContributor(u.id || u._id || u.userId),
                                                                    })
                                                                    : undefined
                                                            }
                                                            removeLoading={removeContributorPending}
                                                        />
                                                    </span>
                                                ))}

                                                {teamMembersExcludingManager.length > maxVisibleMembers && (
                                                    <UserAvatar
                                                        name="More Members"
                                                        role="View all team members"
                                                        initials={
                                                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', fontSize: 13, fontWeight: 700, color: '#ffffff' }}>
                                                                <PlusOutlined style={{ fontSize: 11 }} />
                                                                {teamMembersExcludingManager.length - maxVisibleMembers}
                                                            </span>
                                                        }
                                                        avatarBg="#7543DC"
                                                        size={50}
                                                        stacked={true}
                                                        onClick={() => setMembersModalOpen(true)}
                                                    />
                                                )}

                                                {/* Premium Add Member Avatar using the UserAvatar component itself inside the stack */}
                                                <UserAvatar
                                                    name="Add Member"
                                                    role="Invite Team Contributor"
                                                    initials={<PlusOutlined style={{ color: '#0284c7', fontSize: 20 }} />}
                                                    avatarBg="#e0f2fe"
                                                    size={50}
                                                    stacked={true}
                                                    onClick={openInviteModal}
                                                />
                                            </AvatarStackContainer>
                                        </Container>
                                    )}
                                </Container>

                                {/* Client Row - Always on next line */}
                                {canViewClientLine && (
                                    <Container $borderTop="1px solid var(--ant-color-border-secondary, #f0f0f0)" $marginTop="20px" $paddingTop="20px" $display="flex" $alignItems="center" $gap="12px">
                                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)', width: 120 }}>Client :</span>
                                        {(clientUser.name || clientUser.email) ? (
                                            <UserAvatar
                                                name={clientUser.name || clientUser.email}
                                                role="Client Partner"
                                                imageUrl={clientUser.profileImage ? resolveProfileImageUrl(clientUser.profileImage, ENV.API_URL) : null}
                                                initials={getInitials(clientUser.name || clientUser.email)}
                                                avatarBg="var(--ant-color-primary, #1890ff)"
                                                size={50}
                                            />
                                        ) : (
                                            <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>N/A</span>
                                        )}
                                    </Container>
                                )}
                            </ContentCard>

                            {/* Team Activity and Team Workload 2-Column Grid */}
                            <TwoColumnGrid style={{ gap: 20, marginBottom: 20 }}>
                                <TeamActivityCard
                                    activities={mappedActivities}
                                    onSeeMore={() => setActivityPanelOpen(true)}
                                    title={canViewAllActivity ? 'Team Activity' : 'My Activity'}
                                />
                                <TeamWorkloadCard
                                    workloads={mappedWorkloads}
                                    title={canViewAllWorkload ? 'Team Workload' : 'My Workload'}
                                />
                            </TwoColumnGrid>
                        </>
                    )}

                    {/* Description Tab Content */}
                    {activeTab === 'description' && (
                        <ContentCard $padding="24px" $marginBottom="24px">
                            <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginBottom="24px" $paddingBottom="16px" style={{ borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)' }}>
                                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--ant-color-text, #111827)', fontFamily: 'var(--font-family-geist)' }}>
                                    Project Description
                                </h3>
                                {!isEditingDetail && showDetailAndAttachmentActions && (
                                    descriptionDraft !== null ? (
                                        <SegmentedButton
                                            segments={[
                                                {
                                                    label: "Draft Continue",
                                                    icon: <EditOutlined style={{ fontSize: 13 }} />,
                                                    onClick: () => setIsEditingDetail(true),
                                                    colors: {
                                                        background: colors.primary[600],
                                                        color: '#ffffff',
                                                        hoverBackground: colors.primary[100],
                                                        hoverColor: '#000000'
                                                    }
                                                },
                                                {
                                                    label: "Discard",
                                                    onClick: handleResetDraft,
                                                    colors: {
                                                        background: colors.error[600],
                                                        color: '#ffffff',
                                                        hoverBackground: colors.error[100],
                                                        hoverColor: '#000000'
                                                    }
                                                }
                                            ]}
                                            $size="13px"
                                            $padding="0 12px"
                                            $minHeight="32px"
                                            $chrome="split"
                                            $autoWidth={true}
                                            style={{
                                                borderRadius: '6px',
                                                border: 'none',
                                                background: 'transparent',
                                                overflow: 'hidden'
                                            }}
                                        />
                                    ) : (
                                        <Button
                                            type="button"
                                            $primary={false}
                                            onClick={() => setIsEditingDetail(true)}
                                            style={{
                                                background: 'var(--ant-color-fill-quaternary, #f3f4f6)',
                                                border: 'none',
                                                boxShadow: 'none',
                                                color: 'var(--ant-color-text, #111827)',
                                                borderRadius: 6,
                                                padding: '4px 12px',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                height: 32
                                            }}
                                        >
                                            {hasContent ? (
                                                <>
                                                    <EditOutlined style={{ fontSize: 14 }} /> Edit
                                                </>
                                            ) : (
                                                <>
                                                    <PlusOutlined style={{ fontSize: 14 }} /> Add
                                                </>
                                            )}
                                        </Button>
                                    )
                                )}
                            </Container>

                            <Container $width="100%" $maxWidth="100%" style={{ display: 'flex', flexDirection: 'column' }}>
                                <ProjectDescriptionContent
                                    hasContent={hasContent}
                                    isEditingDetail={isEditing}
                                    showDetailAndAttachmentActions={showDetailAndAttachmentActions}
                                    setIsEditingDetail={setIsEditingDetail}
                                    editContent={editContent}
                                    setEditContent={setEditContent}
                                    isInvitedCollaborator={isInvitedCollaborator}
                                    mentionUsers={mentionUsers}
                                    mentionMilestones={mentionMilestones}
                                    mentionSprints={mentionSprints}
                                    handlePostFilesSelected={handlePostFilesSelected}
                                    handleLinkRequest={handleLinkRequest}
                                    pendingPostAttachments={pendingPostAttachments}
                                    removePendingPostAttachment={removePendingPostAttachment}
                                    showProjectDetailsAsMarkdown={showProjectDetailsAsMarkdown}
                                    projectDetailsContentLinked={projectDetailsContentLinked}
                                    projectDetailsContent={projectDetailsContent}
                                    detailsMarkdownAttachmentItems={detailsMarkdownAttachmentItems}
                                    handleMarkdownDescriptionLinkClick={handleMarkdownDescriptionLinkClick}
                                    pendingLinkAttachments={pendingLinkAttachments}
                                    removePendingLinkAttachment={removePendingLinkAttachment}
                                    setPreviewImageUrl={setPreviewImageUrl}
                                    descriptionDraft={descriptionDraft}
                                    handleResetDraft={handleResetDraft}
                                    deletedSavedUrls={deletedSavedUrls}
                                    handleDeleteSavedAttachment={handleDeleteSavedAttachment}
                                />
                            </Container>

                            {isEditing && (
                                <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginTop="20px">
                                    <Container>
                                        {descriptionDraft !== null && (
                                            <Button
                                                type="button"
                                                $primary={false}
                                                $size="14px"
                                                onClick={handleResetDraft}
                                                style={{
                                                    color: '#ff4d4f',
                                                    borderColor: 'rgba(255, 77, 79, 0.3)',
                                                    background: 'transparent',
                                                    transition: 'all 0.2s',
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = 'rgba(255, 77, 79, 0.05)';
                                                    e.currentTarget.style.borderColor = '#ff4d4f';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 77, 79, 0.3)';
                                                }}
                                            >
                                                Reset to Original
                                            </Button>
                                        )}
                                    </Container>
                                    <Container $display="flex" $alignItems="center" $gap="8px">
                                        <Button type="button" $primary $blackOnLight $size="14px" loading={savePending} onClick={handleSaveDetail}>
                                            Save
                                        </Button>
                                        <Button type="button" $primary={false} $size="14px" onClick={handleCancelEdit}>
                                            Cancel
                                        </Button>
                                    </Container>
                                </Container>
                            )}
                        </ContentCard>
                    )}

                    {/* Attachments Tab Content */}
                    {activeTab === 'attachments' && (
                        <ContentCard $padding="24px" $marginBottom="24px">
                            <Container $width="100%" $maxWidth="100%" $minHeight="150px">
                                <ProjectAttachmentsPanel
                                    isClientRole={isClientRole}
                                    isInvitedCollaborator={isInvitedCollaborator}
                                    handleAddAttachUrl={handleAddAttachUrl}
                                    linkAttachments={linkAttachments}
                                    attachUrlsExpanded={attachUrlsExpanded}
                                    setAttachUrlsExpanded={setAttachUrlsExpanded}
                                    fileAttachments={fileAttachments}
                                    uploadAttachmentsPending={uploadAttachmentsPending}
                                    handleAttachmentAddClick={handleAttachmentAddClick}
                                    attachmentInputRef={attachmentInputRef}
                                    handleAttachmentFileChange={handleAttachmentFileChange}
                                    handleAttachmentClick={handleAttachmentClick}
                                    onDeleteAttachment={handleDeleteAttachmentRedirect}
                                    onDeleteLink={handleDeleteLinkRedirect}
                                />
                            </Container>
                        </ContentCard>
                    )}
                </Container>
            </Container>

            <SidePanel
                open={profilePanelOpen}
                onClose={() => setProfilePanelOpen(false)}
                title="User Profile Detail"
                width={480}
            >
                {selectedUserFallback && (
                    <Container $display="flex" $flexDirection="column" $gap="24px" style={{ padding: '8px 4px' }}>
                        {/* Header Profile Card */}
                        <Container
                            $display="flex"
                            $flexDirection="column"
                            $alignItems="center"
                            $padding="24px"
                            style={{
                                background: 'linear-gradient(135deg, rgba(117, 67, 220, 0.05) 0%, rgba(117, 67, 220, 0.02) 100%)',
                                borderRadius: 16,
                                border: '1px solid rgba(117, 67, 220, 0.1)',
                                textAlign: 'center',
                                position: 'relative'
                            }}
                        >
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <UserAvatar
                                    name={detailedUser?.name || selectedUserFallback.name || selectedUserFallback.email}
                                    role={detailedUser?.roles?.[0]?.name || getMemberRoleLabel(selectedUserFallback)}
                                    imageUrl={(detailedUser?.profileImage || selectedUserFallback.profileImage) ? resolveProfileImageUrl(detailedUser?.profileImage || selectedUserFallback.profileImage, ENV.API_URL) : null}
                                    initials={getInitials(detailedUser?.name || selectedUserFallback.name || selectedUserFallback.email)}
                                    avatarBg={getAvatarColor(selectedUserFallback.id)}
                                    size={96}
                                    disableExpand={true}
                                />
                                {can('projects.members.manage') && String(detailedUser?.id || selectedUserFallback.id) !== String(project?.createdBy) && (
                                    <Popconfirm
                                        title="Remove Member"
                                        description="Are you sure you want to remove this member from the project?"
                                        onConfirm={() => {
                                            removeContributor(detailedUser?.id || selectedUserFallback.id, {
                                                onSuccess: () => setProfilePanelOpen(false),
                                            });
                                        }}
                                        okText="Yes"
                                        cancelText="No"
                                        okButtonProps={{ danger: true, loading: removeContributorPending }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: '#ff4d4f',
                                                color: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                                                transition: 'all 0.2s ease',
                                                zIndex: 10,
                                            }}
                                            className="member-remove-dot"
                                            title="Remove from project"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <CloseOutlined />
                                        </div>
                                    </Popconfirm>
                                )}
                            </div>
                            
                            <h3 style={{
                                fontSize: 20,
                                fontWeight: 700,
                                margin: '16px 0 4px 0',
                                color: 'var(--ant-color-text, #111827)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                justifyContent: 'center'
                            }}>
                                {detailedUser?.name || selectedUserFallback.name || 'Anonymous User'}
                                {detailedUser?.verified && (
                                    <Tooltip title="Verified Account">
                                        <CheckCircleFilled style={{ color: '#10b981', fontSize: 16 }} />
                                    </Tooltip>
                                )}
                            </h3>
                            <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, #6b7280)', wordBreak: 'break-all' }}>
                                {detailedUser?.email || selectedUserFallback.email}
                            </span>
                            
                            <span style={{
                                marginTop: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 12px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 600,
                                background: '#eff6ff',
                                color: '#1d4ed8',
                                border: '1px solid #bfdbfe',
                            }}>
                                {detailedUser?.roles?.[0]?.name || getMemberRoleLabel(selectedUserFallback) || 'Contributor'}
                            </span>
                        </Container>

                        {/* Loading State or Details Content */}
                        {loadingUser && !detailedUser ? (
                            <Container $display="flex" $justifyContent="center" $alignItems="center" style={{ padding: '40px 0' }}>
                                <Spin size="large" />
                            </Container>
                        ) : (
                            <Container $display="flex" $flexDirection="column" $gap="16px">
                                <Container $display="flex" $flexDirection="column" $gap="12px" style={{
                                    padding: '4px 0 16px',
                                }}>
                                    {/* Plan rendered as elegant icon badge */}
                                    <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginBottom="12px">
                                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Platform Plan</span>
                                        {detailedUser?.plan ? (
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '4px 10px',
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                ...(
                                                    String(detailedUser.plan.key || '').toLowerCase() === 'starter' ? { background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' } :
                                                    String(detailedUser.plan.key || '').toLowerCase() === 'pro' || String(detailedUser.plan.key || '').toLowerCase() === 'premium' ? { background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' } :
                                                    String(detailedUser.plan.key || '').toLowerCase() === 'enterprise' ? { background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' } :
                                                    { background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }
                                                )
                                            }}>
                                                {String(detailedUser.plan.key || '').toLowerCase() === 'starter' && <RocketOutlined style={{ marginRight: 6 }} />}
                                                {['pro', 'premium'].includes(String(detailedUser.plan.key || '').toLowerCase()) && <CrownOutlined style={{ marginRight: 6 }} />}
                                                {String(detailedUser.plan.key || '').toLowerCase() === 'enterprise' && <ThunderboltOutlined style={{ marginRight: 6 }} />}
                                                {detailedUser.plan.name || 'Standard'}
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Standard</span>
                                        )}
                                    </Container>

                                    <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginBottom="12px" style={{ borderTop: '1px solid var(--ant-color-border-secondary, #f0f0f0)', paddingTop: 12 }}>
                                        <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Account Status</span>
                                        <span style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            padding: '2px 8px',
                                            borderRadius: 6,
                                            background: detailedUser?.status === 'suspended' ? '#fef2f2' : '#ecfdf5',
                                            color: detailedUser?.status === 'suspended' ? '#ef4444' : '#059669',
                                            textTransform: 'capitalize',
                                        }}>
                                            {detailedUser?.status || 'active'}
                                        </span>
                                    </Container>

                                    <Container $display="flex" $justifyContent="space-between" $alignItems="center" $marginBottom="12px" style={{ borderTop: '1px solid var(--ant-color-border-secondary, #f0f0f0)', paddingTop: 12 }}>
                                        <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Platform Joining Date</span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                            {formatDate(detailedUser?.createdAt)}
                                        </span>
                                    </Container>

                                    <Container $display="flex" $justifyContent="space-between" $alignItems="center" style={{ borderTop: '1px solid var(--ant-color-border-secondary, #f0f0f0)', paddingTop: 12 }}>
                                        <span style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, #6b7280)' }}>Last Login Session</span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                            {formatDate(detailedUser?.lastLogin || detailedUser?.updatedAt)}
                                        </span>
                                    </Container>
                                </Container>
                            </Container>
                        )}
                    </Container>
                )}
            </SidePanel>

            <TeamActivityPanel
                projectId={projectId}
                open={activityPanelOpen}
                onClose={() => setActivityPanelOpen(false)}
                assignedUsers={assignedUsers}
            />

            <ProjectMembersModal
                open={membersModalOpen}
                onCancel={() => {
                    setMembersModalOpen(false);
                    setMembersSearch('');
                }}
                search={membersSearch}
                onSearchChange={setMembersSearch}
                visibleAssignedUsers={visibleAssignedUsers}
                canManageMembers={can('projects.members.manage')}
                projectCreatorId={project?.createdBy}
                onRemoveMember={removeContributor}
                removePending={removeContributorPending}
            />

            <AddUrlModal
                open={linkModalOpen}
                onCancel={() => setLinkModalOpen(false)}
                form={linkModalForm}
                onSubmit={handleLinkModalSubmit}
            />

            <AddUrlModal
                open={attachUrlModalOpen}
                onCancel={() => {
                    setAttachUrlModalOpen(false);
                    attachUrlForm.resetFields();
                }}
                form={attachUrlForm}
                onSubmit={handleAttachUrlModalSubmit}
                uploadAttachmentsPending={uploadAttachmentsPending}
            />

            <InviteMemberModal
                open={inviteModalOpen}
                onCancel={closeInviteModal}
                form={inviteForm}
                inviteSelectOptions={inviteSelectOptions}
                filterSelectOption={filterSelectOption}
                addContributorsPending={addContributorsPending}
                onSubmit={handleInviteSubmit}
                onCreateUserClick={openCreateUserModal}
                canCreateUser={canCreateUser}
            />

            <CreateUserFormModal
                open={createUserModalOpen}
                onCancel={closeCreateUserModal}
                form={createForm}
                roleOptions={roleSelectOptions}
                onSubmit={handleCreateUserSubmit}
                loading={createUserPending}
                permissionDenied={!canCreateUser}
            />

            <Image
                style={{ display: 'none' }}
                src={previewImageUrl || undefined}
                preview={{
                    visible: !!previewImageUrl,
                    src: previewImageUrl || undefined,
                    rootClassName: 'global-image-preview-root',
                    onVisibleChange: (visible) => {
                        if (!visible) setPreviewImageUrl('');
                    },
                }}
            />
        </DetailsTabLayout>
    );
}
